import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, MapPin, User, Tag } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../contexts/LanguageContext';
import { publications, getPublicationBySlug } from '../data/publicationsData';

const pickLang = (obj, lang) => {
  if (obj == null) return '';
  if (typeof obj === 'string') return obj;
  return obj[lang] || obj.fr || obj.en || '';
};

const PublicationArticlePage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { slug } = useParams();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  // Fallback to the first article if slug is missing/unknown
  const pub = getPublicationBySlug(slug) || publications[0];

  const publishedDate = new Date(pub.date).toLocaleDateString(
    language === 'fr' ? 'fr-FR' : 'en-GB',
    { day: 'numeric', month: 'long', year: 'numeric' }
  );

  const H = ({ children }) => (
    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-12 mb-4">{children}</h2>
  );
  const P = ({ children }) => (
    <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-5">{children}</p>
  );

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <main className="pt-[240px]" data-testid="publication-article-page">
        {/* Hero */}
        <section className="relative">
          <div className="relative h-[380px] md:h-[520px] overflow-hidden">
            <img
              src={pub.cover}
              alt={pickLang(pub.title, language)}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
            <div className="absolute inset-0 flex items-end">
              <div className="max-w-5xl mx-auto px-6 pb-10 md:pb-14 w-full">
                <div className="flex flex-wrap items-center gap-3 text-white/85 text-xs md:text-sm mb-4">
                  <span
                    className="inline-block px-3 py-1 rounded-sm font-bold tracking-widest uppercase text-white"
                    style={{ backgroundColor: '#FF8C00' }}
                    data-testid="article-tag"
                  >
                    {pickLang(pub.tag, language)}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" /> {publishedDate}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="w-4 h-4" /> {pub.location}
                  </span>
                </div>
                <h1
                  className="text-white text-3xl sm:text-4xl md:text-5xl font-bold leading-tight max-w-4xl"
                  data-testid="article-title"
                >
                  {pickLang(pub.title, language)}
                </h1>
              </div>
            </div>
          </div>
        </section>

        {/* Article body */}
        <section className="py-14 md:py-20">
          <div className="max-w-4xl mx-auto px-6">
            <button
              onClick={() => navigate('/actualites')}
              className="inline-flex items-center gap-2 text-gray-500 hover:text-orange-500 mb-8 transition-colors text-sm font-semibold"
              data-testid="article-back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              {language === 'fr' ? 'Toutes les publications' : 'All publications'}
            </button>

            {/* Lead */}
            <p
              className="text-lg md:text-xl text-gray-800 leading-relaxed font-medium border-l-4 pl-6 mb-10"
              style={{ borderColor: '#702963' }}
              data-testid="article-intro"
            >
              {pickLang(pub.intro, language)}
            </p>

            {/* Stats */}
            {pub.stats?.length ? (
              <div
                className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-12"
                data-testid="article-stats"
              >
                {pub.stats.map((s, i) => (
                  <div
                    key={i}
                    className="bg-gray-50 border border-gray-200 p-4 md:p-5 text-center rounded-sm"
                  >
                    <div className="text-2xl md:text-4xl font-bold" style={{ color: '#702963' }}>
                      {s.value}
                      {s.unit && (
                        <span className="text-base md:text-xl ml-1 font-semibold">
                          {pickLang(s.unit, language)}
                        </span>
                      )}
                    </div>
                    <div className="text-xs md:text-sm text-gray-600 mt-1">
                      {pickLang(s.label, language)}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            {/* Sections */}
            {pub.sections?.map((sec, idx) => (
              <div key={idx} data-testid={`article-section-${idx}`}>
                <H>{pickLang(sec.title, language)}</H>
                {(sec.paragraphs?.[language] || sec.paragraphs?.fr || []).map((p, j) => (
                  <P key={j}>{p}</P>
                ))}
                {typeof sec.image === 'number' && pub.images?.[sec.image] ? (
                  <figure className="my-10">
                    <img
                      src={pub.images[sec.image]}
                      alt={pickLang(sec.imageCaption, language)}
                      className="w-full rounded-sm shadow-xl object-cover"
                      loading="lazy"
                      data-testid={`article-image-${sec.image}`}
                    />
                    {sec.imageCaption && (
                      <figcaption className="text-xs md:text-sm text-gray-500 mt-2 italic text-center">
                        {pickLang(sec.imageCaption, language)}
                      </figcaption>
                    )}
                  </figure>
                ) : null}
              </div>
            ))}

            {/* Quote */}
            {pub.quote && (
              <blockquote
                className="my-10 border-l-4 pl-6 py-2 italic text-gray-800 text-lg md:text-xl leading-relaxed"
                style={{ borderColor: '#FF8C00' }}
                data-testid="article-quote"
              >
                « {pickLang(pub.quote.text, language)} »
                <footer className="mt-3 not-italic text-sm text-gray-500 font-semibold">
                  — {pickLang(pub.quote.author, language)}
                </footer>
              </blockquote>
            )}

            {/* Conclusion */}
            {pub.conclusion && (
              <div data-testid="article-conclusion">
                <H>{pickLang(pub.conclusion.title, language)}</H>
                {(pub.conclusion.paragraphs?.[language] || pub.conclusion.paragraphs?.fr || []).map(
                  (p, i) => (
                    <P key={i}>{p}</P>
                  )
                )}
              </div>
            )}

            {/* Meta */}
            <div className="mt-12 pt-6 border-t border-gray-200 flex flex-wrap items-center gap-4 text-sm text-gray-500">
              <span className="inline-flex items-center gap-2">
                <User className="w-4 h-4" />
                {language === 'fr' ? 'Rédaction GCI' : 'GCI Newsroom'}
              </span>
              {pub.tagsBottom && (
                <span className="inline-flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  {pickLang(pub.tagsBottom, language)}
                </span>
              )}
            </div>
          </div>
        </section>

        {/* Related */}
        {publications.length > 1 && (
          <section className="py-14 bg-gray-50" data-testid="article-related">
            <div className="max-w-6xl mx-auto px-6">
              <h3
                className="text-xl md:text-2xl font-bold mb-8"
                style={{ color: '#702963' }}
              >
                {language === 'fr' ? 'Aussi à lire' : 'Also read'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {publications
                  .filter((p) => p.slug !== pub.slug)
                  .slice(0, 3)
                  .map((p) => (
                    <Link
                      key={p.slug}
                      to={`/actualites/${p.slug}`}
                      className="group block bg-white border border-gray-200 rounded-sm overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                      data-testid={`related-${p.slug}`}
                    >
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img
                          src={p.cover}
                          alt={pickLang(p.title, language)}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <span
                          className="absolute top-3 left-3 inline-block px-3 py-1 rounded-sm font-bold tracking-widest uppercase text-white text-xs"
                          style={{ backgroundColor: '#FF8C00' }}
                        >
                          {pickLang(p.tag, language)}
                        </span>
                      </div>
                      <div className="p-5">
                        <h4 className="text-base font-bold text-gray-900 leading-snug group-hover:text-[#702963] transition-colors">
                          {pickLang(p.title, language)}
                        </h4>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-16 bg-white" data-testid="article-cta">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-2xl md:text-4xl font-bold mb-4" style={{ color: '#702963' }}>
              {language === 'fr' ? 'Un projet à mener à Kinshasa ?' : 'A project to run in Kinshasa?'}
            </h2>
            <p className="text-gray-700 text-base md:text-lg mb-8 max-w-2xl mx-auto">
              {language === 'fr'
                ? 'Nos ingénieurs et architectes étudient gratuitement la faisabilité de votre projet et vous proposent un devis détaillé sous 7 jours ouvrés.'
                : 'Our engineers and architects study the feasibility of your project free of charge and provide a detailed quote within 7 business days.'}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/votre-projet"
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-3 rounded-sm transition-all duration-300 transform hover:scale-105"
                data-testid="article-cta-project"
              >
                {language === 'fr' ? 'Démarrer mon projet' : 'Start my project'}
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/actualites"
                className="inline-flex items-center gap-2 border-2 border-[#702963] text-[#702963] font-semibold px-8 py-3 hover:bg-[#702963] hover:text-white transition-all"
                data-testid="article-cta-more"
              >
                {language === 'fr' ? 'Voir plus de publications' : 'See more publications'}
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PublicationArticlePage;
